/**
 * @file ProfitAnalysisReportOne.tsx
 * @description 수익 분석 1페이지 Executive Summary 리포트 컴포넌트
 *
 * 주요 기능:
 * 1. A4 1페이지에 압축된 수익 분석 통합 리포트 표시
 * 2. 상단(KPI) - 중단(Split Grid) - 하단(Footer) 구조
 * 3. Key Metrics: Equity, Net Profit, ROI 핵심 지표
 * 4. Cost & Funding: 비용 구조 테이블 + 자금 조달 플랜 프로그레스 바
 * 5. Scenario & Strategy: 시나리오 분석 + 세금 전략 테이블
 * 6. Analysis Note 유지
 *
 * 핵심 구현 로직:
 * - 기존 ProfitAnalysisReport.tsx의 로직과 데이터 모두 가져오기
 * - A4 Portrait 고정 사이즈 (210mm x 297mm)
 * - Ledger(장부) 스타일 디자인
 *
 * @dependencies
 * - @/lib/types: Profit, Valuation, Costs, AuctionSummary 타입
 * - @/lib/utils/number: formatCurrency, formatPercentage
 * - @/lib/domain/auction/utils/tax-calculation: calculateTransferTax
 * - @/lib/domain/auction/utils/funding-schedule: calculateFundingSchedule
 * - @/components/shared/ui/table: Table 컴포넌트
 * - lucide-react: 아이콘
 *
 * @see {@link /docs/refactoring/22-simulation-scope-matrix.md} - 현재 수익 리포트 유지 범위
 */

"use client";

import { useEffect, useRef } from "react";
import Lenis from "@studio-freight/lenis";
import { Profit, Valuation, Costs, AuctionSummary, Property } from "@/lib/core/types";
import { formatCurrency, formatPercentage } from "@/lib/utils/number";
import { calculateTransferTax } from "@/lib/domain/auction/utils/tax-calculation";
import defaultPolicy from "@/lib/core/policy/defaultpolicy";

export interface ProfitAnalysisReportOneProps {
  profit: Profit;
  valuation: Valuation;
  costs: Costs;
  summary: AuctionSummary;
  property: Property;
  userBid: number;
  simulationId: string;
  outcome?: "success" | "failed"; // 낙찰 결과 (선택적)
  isSample?: boolean; // 샘플 리포트 여부 (샘플 리포트는 GA 이벤트 전송 안 함)
}

export function ProfitAnalysisReportOne({
  profit,
  valuation,
  costs,
  summary,
  property,
  userBid,
  simulationId,
  outcome, // eslint-disable-line @typescript-eslint/no-unused-vars
  isSample = false,
}: ProfitAnalysisReportOneProps) {
  console.group("ProfitAnalysisReportOne Component");
  console.log("Profit data:", {
    initialSafetyMargin: profit.initialSafetyMargin,
    scenarios: {
      "3m": profit.scenarios["3m"],
      "6m": profit.scenarios["6m"],
      "12m": profit.scenarios["12m"],
    },
  });
  console.log("Costs data:", {
    acquisition: costs.acquisition,
    byPeriod: costs.byPeriod,
  });
  console.log("Valuation data:", {
    adjustedFMV: valuation.adjustedFMV,
    exitPrice: valuation.exitPrice,
  });
  console.log("Summary data:", summary);
  console.groupEnd();

  // Policy 가져오기
  const policy = defaultPolicy;

  // 12개월 시나리오 (기본)
  const scenario12m = profit.scenarios["12m"];
  const exitPrice12m = scenario12m.exitPrice;
  const netProfit12m = scenario12m.netProfit;
  const roe12m = scenario12m.roi * 100; // ROE (자기자본이익률)

  // 12개월 보유 시 양도소득세 계산 (세후 순수익)
  const taxResult12m = calculateTransferTax(netProfit12m, 12, policy);
  const afterTaxProfit12m = taxResult12m.afterTaxProfit;

  // 수익 구조 흐름 계산
  const investmentCost = costs.acquisition.totalAcquisition;
  const holdingCost12m =
    costs.byPeriod["12m"].holdingCost + costs.byPeriod["12m"].interestCost;

  // 청구금액 (배당요구 등으로 발생하는 비용)
  const claimAmount = property.claimAmount || 0;

  // 인수권리비용 (매각으로 소멸되지 않는 권리 인수 비용)
  // totalAcquisition = userBid + assumableRights + taxes + legalFees + repairCost + evictionCost + bondDiscount + unpaidMaintenanceFee
  // 따라서 assumableRights = totalAcquisition - userBid - totalAdditionalCosts
  const assumableRightsCost = Math.max(0,
    costs.acquisition.totalAcquisition -
    userBid -
    costs.acquisition.totalAdditionalCosts
  );

  // Analyst Note 텍스트 생성 (수익률 비교 로직)
  const targetRoiPercent = policy.profit.targetAnnualRoi * 100;
  const isHighReturn = roe12m > targetRoiPercent;
  const isNegativeReturn = roe12m < 0;

  let analystNoteText: string;
  if (isNegativeReturn) {
    analystNoteText = `본 물건의 기대 수익률(ROE)은 ${formatPercentage(
      roe12m,
      1,
    )}로, 음수 수익률을 보이고 있어 주의가 필요한 투자입니다. 낙찰가 대비 비용이 높거나 시세 하락 가능성을 면밀히 검토해야 합니다. 총 매입비용은 약 ${formatCurrency(
      investmentCost,
    )}이지만, 실제 귀하가 투입해야 할 현금(Equity)은 약 ${formatCurrency(
      costs.acquisition.ownCash,
    )} 수준입니다.`;
  } else if (isHighReturn) {
    analystNoteText = `본 물건의 기대 수익률(ROE)은 ${formatPercentage(
      roe12m,
      1,
    )}로, 일반적인 부동산 투자 기준선(${formatPercentage(
      targetRoiPercent,
      0,
    )})을 크게 상회하는 고효율 투자처입니다. 이는 낙찰가율이 시세 대비 저렴한 것도 있지만, 무엇보다 [경락잔금대출 레버리지 효과]가 극대화되었기 때문입니다. 총 매입비용은 약 ${formatCurrency(
      investmentCost,
    )}이지만, 실제 귀하가 투입해야 할 현금(Equity)은 약 ${formatCurrency(
      costs.acquisition.ownCash,
    )} 수준이므로 자본 효율성이 매우 뛰어납니다.`;
  } else {
    analystNoteText = `본 물건의 기대 수익률(ROE)은 ${formatPercentage(
      roe12m,
      1,
    )}로, 일반적인 부동산 투자 기준선(${formatPercentage(
      targetRoiPercent,
      0,
    )})에 미달합니다. 추가적인 수익 개선 방안을 검토하거나 투자 재검토가 필요할 수 있습니다. 총 매입비용은 약 ${formatCurrency(
      investmentCost,
    )}이며, 실제 귀하가 투입해야 할 현금(Equity)은 약 ${formatCurrency(
      costs.acquisition.ownCash,
    )} 수준입니다.`;
  }

  // 최종 투자 가치 평가 텍스트 생성 (수익률 비교 로직) - 현재 사용되지 않음

  // 초기 필요 자금 계산
  const realInvestment = costs.acquisition.ownCash;

  // 자금 투입 스케줄 - 현재 사용되지 않음

  // 양도소득세 시뮬레이션 (Plan A/B/C)
  const scenario6m = profit.scenarios["6m"];
  const transferGain6m = scenario6m.netProfit;

  // 24개월과 36개월은 Exit Price를 12개월과 동일하다고 가정하고 totalCost를 계산
  const holdingCost24m = costs.byPeriod["12m"].holdingCost * 2;
  const interestCost24m = costs.byPeriod["12m"].interestCost * 2;
  const totalCost24m =
    costs.acquisition.totalAcquisition + holdingCost24m + interestCost24m;
  const transferGain24m = exitPrice12m - totalCost24m;

  const holdingCost36m = costs.byPeriod["12m"].holdingCost * 3;
  const interestCost36m = costs.byPeriod["12m"].interestCost * 3;
  const totalCost36m =
    costs.acquisition.totalAcquisition + holdingCost36m + interestCost36m;
  const transferGain36m = exitPrice12m - totalCost36m;

  const taxScenarioA = calculateTransferTax(transferGain6m, 6);
  const taxScenarioB = calculateTransferTax(transferGain24m, 24);
  const taxScenarioC = calculateTransferTax(transferGain36m, 36);
  const taxScenarios = [taxScenarioA, taxScenarioB, taxScenarioC];

  console.log("양도소득세 시뮬레이션 계산 결과:", {
    transferGain6m,
    transferGain24m,
    transferGain36m,
    totalCost24m,
    totalCost36m,
    exitPrice12m,
    taxScenarios: taxScenarios.map((s) => ({
      planType: s.planType,
      taxRate: s.taxRate,
      taxAmount: s.taxAmount,
      afterTaxProfit: s.afterTaxProfit,
    })),
  });

  // 최적 전략 계산 (세후 순이익 기준) - 현재 사용되지 않음

  // 최종 투자 가치 평가 Score (grade 기반, Policy에서 읽기) - 현재 사용되지 않음
  // const profitScore = policy.profit.gradeToScore?.[summary.grade] ?? 88;

  // Lenis 스크롤 적용
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;

    // Lenis 인스턴스 생성
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
    });

    // 컨테이너에 Lenis 적용
    lenis.scrollTo(0, { immediate: true });

    // requestAnimationFrame을 사용하여 Lenis 업데이트
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // 컴포넌트 언마운트 시 정리
    return () => {
      lenis.destroy();
    };
  }, []);

  // 프리미엄 리포트 조회수 카운팅 (중복 방지)
  useEffect(() => {
    if (isSample) return;

    const trackReportView = async () => {
      const today = new Date().toISOString().split("T")[0];
      const reportKey = `premium_report_view_${today}_profit_one_${simulationId}`;

      if (typeof window !== "undefined") {
        const hasViewed = localStorage.getItem(reportKey);
        if (hasViewed) {
          return;
        }

        try {
          const response = await fetch("/api/simulation/report-view", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              reportType: "profit",
              simulationId: simulationId,
            }),
          });

          if (response.ok) {
            localStorage.setItem(reportKey, "true");
          }
        } catch {
          // 조회수 카운팅 실패 시 조용히 처리
        }
      }
    };

    trackReportView();
  }, [isSample, simulationId]);

  // 시나리오 계산 (Conservative, Standard, Aggressive)
  const exitPriceConservative = exitPrice12m * 0.95; // -5%
  const exitPriceStandard = exitPrice12m; // 0%
  const exitPriceAggressive = exitPrice12m * 1.05; // +5%

  const netProfitConservative = exitPriceConservative - (costs.acquisition.totalAcquisition + holdingCost12m);
  const netProfitStandard = netProfit12m;
  const netProfitAggressive = exitPriceAggressive - (costs.acquisition.totalAcquisition + holdingCost12m);

  // 이미지 저장 및 스크린샷 방지 (데스크톱 + 모바일)
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "F12" ||
        e.key === "PrintScreen" ||
        e.keyCode === 44 ||
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) ||
        (e.ctrlKey && e.key === "U") ||
        (e.ctrlKey && e.key === "S") ||
        (e.ctrlKey && e.shiftKey && e.key === "S") ||
        (e.ctrlKey && e.key === "P")
      ) {
        e.preventDefault();
        alert("스크린샷 및 인쇄가 제한된 콘텐츠입니다.");
        return false;
      }
    };

    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.warn("화면 전환이 감지되었습니다.");
      } else {
        const timeDiff = Date.now() - lastVisibilityChange;
        if (timeDiff < 1000) {
          console.warn("스크린샷이 의심됩니다.");
        }
      }
    };

    // pagehide 이벤트 제거: bfcache를 방해하므로 제거 (성능 최적화)

    const handleOrientationChange = () => {
      console.warn("화면 방향이 변경되었습니다.");
    };

    const handleBlur = () => {
      console.warn("윈도우 포커스가 변경되었습니다.");
    };

    const lastVisibilityChange = Date.now();
    let touchStartTime = 0;
    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartTime = Date.now();
      if (e.touches.length > 0) {
        touchStartY = e.touches[0].clientY;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touchDuration = Date.now() - touchStartTime;
      if (touchDuration < 100 && e.changedTouches.length > 0) {
        const touchEndY = e.changedTouches[0].clientY;
        const deltaY = Math.abs(touchEndY - touchStartY);
        if (deltaY < 10) {
          console.warn("의심스러운 터치 제스처가 감지되었습니다.");
        }
      }
    };

    const handleCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    const handleSelectStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    // pagehide 이벤트 제거: bfcache를 방해하므로 제거 (성능 최적화)
    document.addEventListener("copy", handleCopy);
    document.addEventListener("selectstart", handleSelectStart);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("orientationchange", handleOrientationChange);
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // pagehide 이벤트 제거: bfcache를 방해하므로 제거 (성능 최적화)
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("selectstart", handleSelectStart);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("orientationchange", handleOrientationChange);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="min-h-screen flex justify-center select-none"
      style={{
        fontFamily: "Pretendard, -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
      }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      draggable={false}
    >
      {/* A4 Page Container */}
      <div
        className="bg-white dark:bg-zinc-950 shadow-2xl print:shadow-none print:rounded-none print:p-[12mm] print:min-h-[297mm] print:w-[210mm] print:mx-auto w-full min-h-screen md:w-[210mm] md:h-[297mm] md:min-h-[297mm]"
        style={{
          padding: "12mm",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* 1. Header */}
        <header className="flex justify-between items-end border-b-2 border-zinc-900 dark:border-zinc-100 pb-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-[9px] font-bold px-1.5 py-0.5 rounded">
                PREMIUM
              </span>
              <span className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 tracking-widest uppercase">
                Investment Analysis
              </span>
            </div>
            <h1 className="text-lg sm:text-xl font-black text-zinc-900 dark:text-zinc-100 tracking-tight">
              수익 분석 통합 리포트
            </h1>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-zinc-400 dark:text-zinc-500 font-bold uppercase">
              Simulation Case
            </p>
            <p className="text-sm font-bold text-zinc-700 dark:text-zinc-300">#{simulationId}</p>
          </div>
        </header>

        {/* 2. Key Metrics (Hero) */}
        <section className="mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {/* Equity */}
            <div className="bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-4 rounded-xl">
              <p className="text-xs text-zinc-400 dark:text-zinc-500 font-bold uppercase mb-1">
                Initial Equity (실투자금)
              </p>
              <p className="text-xl sm:text-2xl font-mono font-black text-zinc-900 dark:text-zinc-100">
                {formatCurrency(realInvestment)}
              </p>
            </div>
            {/* Net Profit */}
            <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-800 p-4 rounded-xl relative overflow-hidden">
              <div className="absolute right-0 top-0 w-16 h-16 bg-emerald-100/50 dark:bg-emerald-900/30 rounded-bl-full"></div>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 font-bold uppercase mb-1">
                Net Profit (순수익)
              </p>
              <p className="text-xl sm:text-2xl font-mono font-black text-emerald-600 dark:text-emerald-400">
                {afterTaxProfit12m >= 0 ? "+ " : ""}
                {formatCurrency(afterTaxProfit12m)}
              </p>
            </div>
            {/* ROI */}
            <div className="bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 p-4 rounded-xl">
              <p className="text-xs text-zinc-400 dark:text-zinc-600 font-bold uppercase mb-1">
                ROI (수익률)
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-xl sm:text-2xl font-mono font-black text-emerald-400 dark:text-emerald-600">
                  {formatPercentage(roe12m, 1)}
                </p>
                <span className="text-xs text-zinc-500 dark:text-zinc-600">Annualized</span>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Main Content Grid (Split View) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 mb-6 flex-1">
          {/* LEFT: Cost & Funding (Cols 7) */}
          <div className="col-span-1 lg:col-span-7 flex flex-col gap-6">
            {/* Cost Structure */}
            <div className="border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 dark:bg-zinc-800 px-4 py-2 border-b border-zinc-200 dark:border-zinc-700 flex justify-between items-center">
                <h3 className="text-sm font-bold text-zinc-700 dark:text-zinc-300 uppercase">
                  Cost Structure
                </h3>
                <span className="text-xs text-zinc-400 dark:text-zinc-500">Unit: KRW</span>
              </div>
              <div className="p-2 sm:p-3">
                <table className="w-full">
                  <tbody className="text-xs sm:text-sm">
                    <tr>
                      <td className="font-bold py-1.5 text-sm">낙찰가 (Bid Price)</td>
                      <td className="text-right font-mono font-bold py-1.5">
                        {formatCurrency(userBid)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1.5 text-zinc-500">
                        (+) 취득세 ({(policy.cost.acquisitionTaxRate * 100).toFixed(1)}%)
                      </td>
                      <td className="text-right font-mono text-zinc-500 py-1.5">
                        {formatCurrency(costs.acquisition.taxes)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1.5 text-zinc-500">(+) 법무비 / 채권할인</td>
                      <td className="text-right font-mono text-zinc-500 py-1.5">
                        {formatCurrency(
                          costs.acquisition.legalFees + (costs.acquisition.bondDiscount || 0)
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1.5 text-red-500">
                        (+) 명도비 / 수리비{" "}
                        <span className="text-[9px] text-red-400 bg-red-50 px-1 rounded ml-1">
                          Risk
                        </span>
                      </td>
                      <td className="text-right font-mono text-red-500 py-1.5">
                        {formatCurrency(
                          costs.acquisition.evictionCost +
                            costs.acquisition.repairCost +
                            (costs.acquisition.unpaidMaintenanceFee || 0)
                        )}
                      </td>
                    </tr>
                    {assumableRightsCost > 0 && (
                      <tr>
                        <td className="py-1.5 text-orange-600">
                          (+) 인수권리비용{" "}
                          <span className="text-[9px] text-orange-400 bg-orange-50 px-1 rounded ml-1">
                            필수
                          </span>
                        </td>
                        <td className="text-right font-mono text-orange-600 py-1.5">
                          {formatCurrency(assumableRightsCost)}
                        </td>
                      </tr>
                    )}
                    {claimAmount > 0 && (
                      <tr>
                        <td className="py-1.5 text-blue-600">
                          (+) 청구금액{" "}
                          <span className="text-[9px] text-blue-400 bg-blue-50 px-1 rounded ml-1">
                            배당
                          </span>
                        </td>
                        <td className="text-right font-mono text-blue-600 py-1.5">
                          {formatCurrency(claimAmount)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td className="py-1.5 text-zinc-500">(+) 보유 이자 (12개월)</td>
                      <td className="text-right font-mono text-zinc-500 py-1.5">
                        {formatCurrency(holdingCost12m)}
                      </td>
                    </tr>
                    <tr className="bg-zinc-50">
                      <td className="font-black text-zinc-800 py-2">
                        총 취득원가 (Total Cost)
                      </td>
                      <td className="text-right font-mono font-black text-zinc-900 text-sm py-2">
                        {formatCurrency(investmentCost + holdingCost12m)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Funding Plan */}
            <div className="border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 dark:bg-zinc-800 px-4 py-2 border-b border-zinc-200 dark:border-zinc-700">
                <h3 className="text-sm font-bold text-zinc-700 dark:text-zinc-300 uppercase">
                  Funding Plan
                </h3>
              </div>
              <div className="p-3 sm:p-4 space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-bold text-zinc-600 dark:text-zinc-300">
                    대출금 (LTV {Math.round((costs.acquisition.loanPrincipal / userBid) * 100)}%)
                  </span>
                  <span className="font-mono font-bold text-blue-600 dark:text-blue-400 text-base">
                    {formatCurrency(costs.acquisition.loanPrincipal)}
                  </span>
                </div>
                <div className="w-full bg-zinc-100 dark:bg-zinc-700 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-blue-500 dark:bg-blue-400 h-full"
                    style={{
                      width: `${Math.round((costs.acquisition.loanPrincipal / userBid) * 100)}%`,
                    }}
                  ></div>
                </div>
                <div className="flex justify-between items-center text-sm pt-1">
                  <span className="font-bold text-zinc-600 dark:text-zinc-300">필요 현금 (Equity)</span>
                  <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 text-base">
                    {formatCurrency(realInvestment)}
                  </span>
                </div>
                <div className="w-full bg-zinc-100 dark:bg-zinc-700 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-emerald-500 dark:bg-emerald-400 h-full"
                    style={{
                      width: `${Math.round((realInvestment / userBid) * 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Scenarios & Tax (Cols 5) */}
          <div className="col-span-1 lg:col-span-5 flex flex-col gap-6">
            {/* Scenarios */}
            <div className="border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden h-full">
              <div className="bg-zinc-50 dark:bg-zinc-800 px-4 py-2 border-b border-zinc-200 dark:border-zinc-700">
                <h3 className="text-sm font-bold text-zinc-700 dark:text-zinc-300 uppercase">
                  Scenario Analysis
                </h3>
              </div>
              <div className="p-2 space-y-2 flex-1">
                {/* Aggressive */}
                <div className="p-2 sm:p-3 rounded border border-emerald-100 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20">
                  <div className="flex justify-between text-xs font-bold text-emerald-700 dark:text-emerald-300 mb-1">
                    <span>AGGRESSIVE</span>
                    <span>+5%</span>
                  </div>
                  <div className="flex justify-between items-end">
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">
                      {formatCurrency(exitPriceAggressive)} 매도
                    </span>
                    <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 text-xs">
                      {netProfitAggressive >= 0 ? "+ " : ""}
                      {formatCurrency(netProfitAggressive)}
                    </span>
                  </div>
                </div>
                {/* Standard */}
                <div className="p-2 sm:p-3 rounded border-2 border-zinc-800 dark:border-zinc-200 bg-white dark:bg-zinc-900 shadow-md relative z-10">
                  <div className="flex justify-between text-xs font-bold text-zinc-900 dark:text-zinc-100 mb-1">
                    <span>STANDARD</span>
                    <span>0%</span>
                  </div>
                  <div className="flex justify-between items-end">
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">
                      {formatCurrency(exitPriceStandard)} 매도
                    </span>
                    <span className="font-mono font-bold text-zinc-900 dark:text-zinc-100 text-xs">
                      {netProfitStandard >= 0 ? "+ " : ""}
                      {formatCurrency(netProfitStandard)}
                    </span>
                  </div>
                </div>
                {/* Conservative */}
                <div className="p-2 sm:p-3 rounded border border-red-100 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20">
                  <div className="flex justify-between text-xs font-bold text-red-700 dark:text-red-300 mb-1">
                    <span>CONSERVATIVE</span>
                    <span>-5%</span>
                  </div>
                  <div className="flex justify-between items-end">
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">
                      {formatCurrency(exitPriceConservative)} 매도
                    </span>
                    <span className="font-mono font-bold text-red-600 dark:text-red-400 text-xs">
                      {netProfitConservative >= 0 ? "+ " : ""}
                      {formatCurrency(netProfitConservative)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Strategy */}
            <div className="border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 dark:bg-zinc-800 px-4 py-2 border-b border-zinc-200 dark:border-zinc-700">
                <h3 className="text-sm font-bold text-zinc-700 dark:text-zinc-300 uppercase">
                  Exit Strategy
                </h3>
              </div>
              <div className="p-2 sm:p-3">
                <table className="w-full">
                  <tbody className="text-xs sm:text-sm">
                    <tr>
                      <td className="text-zinc-500 dark:text-zinc-400 py-1.5">단기 (1년↓)</td>
                      <td className="text-right font-mono text-red-500 dark:text-red-400 py-1.5">
                        세율 {formatPercentage(taxScenarioA.taxRate * 100, 0)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-zinc-500 dark:text-zinc-400 py-1.5">일반 (2년)</td>
                      <td className="text-right font-mono text-zinc-900 dark:text-zinc-100 py-1.5">
                        세율 {formatPercentage(taxScenarioB.taxRate * 100, 0)}
                      </td>
                    </tr>
                    <tr className="bg-emerald-50/50 dark:bg-emerald-950/20">
                      <td className="font-bold text-emerald-700 dark:text-emerald-300 py-1.5">
                        비과세 (2년↑)
                      </td>
                      <td className="text-right font-mono font-bold text-emerald-600 dark:text-emerald-400 py-1.5">
                        BEST
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Analysis Note */}
        <div className="mb-4">
          <div
            className={`p-3 sm:p-4 rounded-lg text-sm leading-relaxed text-justify mb-4 border-2 ${
              isNegativeReturn
                ? "bg-red-50/80 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-slate-700 dark:text-slate-300"
                : isHighReturn
                ? "bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-slate-700 dark:text-slate-300"
                : "bg-yellow-50/80 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800 text-slate-700 dark:text-slate-300"
            }`}
          >
            <strong
              className={`block mb-2 text-base ${
                isNegativeReturn
                  ? "text-red-900 dark:text-red-300"
                  : isHighReturn
                  ? "text-emerald-900 dark:text-emerald-300"
                  : "text-yellow-900 dark:text-yellow-300"
              }`}
            >
              📊 Analyst Note:
            </strong>
            {analystNoteText}
          </div>
        </div>

        {/* 5. Footer Disclaimer */}
        <footer className="mt-auto pt-4 border-t border-zinc-200 dark:border-zinc-700">
          <div className="flex justify-between items-end">
            <p className="text-xs sm:text-sm text-zinc-400 dark:text-zinc-500 leading-relaxed max-w-full sm:max-w-lg text-justify">
              * 본 리포트는 입력된 가정에 기초한 시뮬레이션 결과이며, 실제 수익을 보장하지 않습니다.
              세금 계산은 일반적인 1가구 1주택 상황을 가정한 것으로,
              개인의 주택 보유 수, 규제 지역 여부, 대출 금리 변동 등에 따라 실제
              결과는 크게 달라질 수 있습니다. 최종 입찰 전 반드시 전문가와 상담하시기 바랍니다.
            </p>
            <div className="text-right">
              <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100">BIDIX Analytics</p>
              <p className="text-xs text-zinc-400 dark:text-zinc-500">
                Generated on {new Date().toLocaleDateString("ko-KR")}
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
